import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp, API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Mail, Phone, Building2, CheckCircle2, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function SuperMessages() {
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const r = await axios.get(`${API}/contact-messages`);
    setMessages(r.data);
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await axios.patch(`${API}/contact-messages/${id}/read`);
    toast.success("Marqué comme lu");
    load();
  };

  const filtered = messages.filter((m) => {
    if (filter === "unread") return !m.read;
    if (filter === "read") return m.read;
    return true;
  });

  const fmt = (d) => new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <div className="space-y-6" data-testid="super-messages">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-[Outfit] font-bold inline-flex items-center gap-3">
            <Inbox className="w-7 h-7 text-[#D96C4A]"/> Messages reçus
          </h1>
          <p className="text-[#5C6B61] mt-1">{unreadCount > 0 ? `${unreadCount} message(s) non lu(s)` : "Aucun nouveau message"}</p>
        </div>
        <div className="flex gap-2">
          {["all", "unread", "read"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filter === f ? "bg-[#1E4631] text-white" : "bg-white border border-[#E5EBE7] text-[#5C6B61] hover:border-[#1E4631]"
              }`}>
              {f === "all" ? `Tous (${messages.length})` : f === "unread" ? `Non lus (${unreadCount})` : `Lus (${messages.length - unreadCount})`}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white p-12 text-center text-[#5C6B61] rounded-xl border border-[#E5EBE7]" data-testid="empty-messages">
          Aucun message
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((m) => (
            <div key={m.id} data-testid={`msg-${m.id}`}
              className={`bg-white rounded-xl border p-5 transition ${
                m.read ? "border-[#E5EBE7]" : "border-[#D96C4A] shadow-md"
              }`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-[Outfit] font-bold text-lg">{m.name}</h3>
                    {!m.read && <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-[#D96C4A] text-white">Nouveau</span>}
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#1E4631]/10 text-[#1E4631]">{m.subject}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-[#5C6B61] flex-wrap">
                    <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1 hover:text-[#1E4631]"><Mail className="w-3 h-3"/>{m.email}</a>
                    {m.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{m.phone}</span>}
                    {m.organization && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3"/>{m.organization}</span>}
                    <span className="ml-auto">{fmt(m.created_at)}</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 rounded-lg bg-[#FDFBF7] text-sm leading-relaxed whitespace-pre-wrap">{m.message}</div>
              {!m.read && (
                <div className="mt-4 flex justify-end">
                  <Button onClick={() => markRead(m.id)} variant="outline" size="sm" data-testid={`msg-read-${m.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1.5"/> Marquer lu
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
